import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Chip, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import PageContainer from '../../../components/PageContainer';
import { EmptyState, KeyValueList, PageHeader, Panel, StatusChip } from '../../../components/ui';
import { AavOrders } from '../../../store/slices';
import { IStore } from '../../../store/types';
import { aavLineItemStatus, aavOrderStatus } from '../../../lib/aavOrderStatus';
import { formatAmount, formatDate } from '../../../lib/formatDate';
import { useStyles } from './styles';

/** API-provided URLs are rendered as links only when they are plain http(s). */
const safeHref = (url: unknown): string | undefined =>
    typeof url === 'string' && /^https?:\/\//i.test(url) ? url : undefined;

const money = (value: unknown, currency?: string | null) => {
    const amount = formatAmount(value as any);
    return amount === '—' ? amount : `${amount} ${currency || ''}`.trim();
};

/** Read-only detail of one AAV Shopify order (`/aav/orders/{id}`). */
const OneAavOrder = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const dispatch = useDispatch();
    const classes = useStyles();

    const { singleOrder, singleStatus } = useSelector<IStore, any>(
        (state) => state.aavOrders || { singleOrder: null, singleStatus: null }
    );

    useEffect(() => {
        if (orderId) {
            dispatch<any>(AavOrders.getAavOrder(orderId));
        }
    }, [orderId, dispatch]);

    const order = singleOrder;
    const back = { to: '/aav-orders', label: 'AAV orders' };

    if (singleStatus === 'loading') {
        return (
            <PageContainer constrained>
                <div className={classes.skeletons} role="status" aria-label="Loading order">
                    <Skeleton animation="wave" height={72} />
                    <Skeleton animation="wave" variant="rounded" height={320} />
                    <Skeleton animation="wave" variant="rounded" height={200} />
                </div>
            </PageContainer>
        );
    }

    if (!order || singleStatus !== 'resolved') {
        return (
            <PageContainer constrained>
                <PageHeader backTo={back} eyebrow="Production" title="AAV order" />
                <Panel>
                    <EmptyState
                        title={singleStatus === 'rejected' ? 'The order could not be loaded' : 'No order to show'}
                        hint={singleStatus === 'rejected' ? 'The AAV service did not answer. Try again in a moment.' : undefined}
                        action={orderId && singleStatus === 'rejected'
                            ? <Button variant="outlined" size="small" onClick={() => dispatch<any>(AavOrders.getAavOrder(orderId))}>Retry</Button>
                            : undefined}
                    />
                </Panel>
            </PageContainer>
        );
    }

    const status = aavOrderStatus(order.statusId, order.status);
    const currency = order.currencyCode;
    const address = order.shippingAddress || order.addresses?.shipping;
    const attributes: [string, unknown][] = order.attributes
        ? Object.entries(order.attributes).filter(([key]) => !key.startsWith('_'))
        : [];
    const customerName = order.customer ? [order.customer.firstName, order.customer.lastName].filter(Boolean).join(' ') : '';

    return (
        <PageContainer constrained>
            <PageHeader
                backTo={back}
                eyebrow="Production"
                title={`Order #${order.orderNumber ?? order.id}`}
                badges={(
                    <>
                        <StatusChip tone={status.tone}>{status.label}</StatusChip>
                        {order.onHold ? <StatusChip tone="warning" dot>On hold</StatusChip> : null}
                    </>
                )}
                meta={(
                    <>
                        <span>Shop <span className={classes.mono}>{order.shopDomain || '—'}</span></span>
                        <span>Created {formatDate(order.createdAt, { time: true })}</span>
                        <span>Updated {formatDate(order.updatedAt, { time: true })}</span>
                    </>
                )}
            />

            <div className={classes.grid}>
                <div className={classes.main}>
                    <Panel title="Line items" badges={<StatusChip size="sm" tone="neutral">{order.lineItems?.length ?? 0}</StatusChip>} padding="none">
                        {order.lineItems?.length > 0 ? (
                            <div className={classes.scroll}>
                                <Table aria-label="Line items">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell>Vendor</TableCell>
                                            <TableCell>Files</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.lineItems.map((item: any) => {
                                            const lineStatus = aavLineItemStatus(item.statusId, item.status);
                                            const mockupUrl = safeHref(item.attributes?._Mockup || item.printfiles?.[0]?.mockupUrl);
                                            const printfileUrl = safeHref(item.printfiles?.[0]?.imageUrl || item.attributes?._Image_Upload);
                                            const printfiles: any[] = Array.isArray(item.printfiles) ? item.printfiles : [];
                                            return (
                                                <TableRow key={item.id}>
                                                    <TableCell className={classes.productCell}>
                                                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                            {mockupUrl ? (
                                                                <Tooltip
                                                                    title={<img className={classes.preview} src={mockupUrl} alt="" />}
                                                                    enterTouchDelay={200}
                                                                    slotProps={{ tooltip: { sx: { maxWidth: 'none', p: 0.5, bgcolor: 'transparent' } } }}
                                                                >
                                                                    <a href={mockupUrl} target="_blank" rel="noreferrer" aria-label="Mockup, opens in a new tab">
                                                                        <img className={classes.thumb} src={mockupUrl} alt="" loading="lazy" />
                                                                    </a>
                                                                </Tooltip>
                                                            ) : (
                                                                <div className={classes.noThumb} aria-hidden="true">No image</div>
                                                            )}
                                                            <div>
                                                                <div className={classes.product}>{item.name || '—'}</div>
                                                                {item.variantTitle && <div className={classes.secondary}>{item.variantTitle}</div>}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className={classes.mono}>{item.sku || '—'}</TableCell>
                                                    <TableCell><StatusChip size="sm" tone={lineStatus.tone}>{lineStatus.label}</StatusChip></TableCell>
                                                    <TableCell className={classes.numeric}>{item.currentQuantity ?? item.quantity ?? '—'}</TableCell>
                                                    <TableCell className={classes.numeric}>{money(item.originalUnitPrice, item.currencyCode || currency)}</TableCell>
                                                    <TableCell className={classes.nowrap}>{item.vendor || '—'}</TableCell>
                                                    <TableCell className={classes.filesCell}>
                                                        {printfiles.length > 0 ? (
                                                            <div className={classes.files}>
                                                                {printfiles.map((pf: any) => {
                                                                    const mockup = safeHref(pf.mockupUrl);
                                                                    const image = safeHref(pf.imageUrl);
                                                                    return (
                                                                        <div key={pf.id} className={classes.fileRow}>
                                                                            {mockup && <a className={classes.fileLink} href={mockup} target="_blank" rel="noreferrer">Mockup</a>}
                                                                            {image && <a className={classes.fileLink} href={image} target="_blank" rel="noreferrer">Print file</a>}
                                                                            {pf.side && <span className={classes.fileSide}>{pf.side}</span>}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : printfileUrl ? (
                                                            <a className={classes.fileLink} href={printfileUrl} target="_blank" rel="noreferrer">Print file</a>
                                                        ) : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div style={{ padding: 20 }} className={classes.secondary}>This order has no line items.</div>
                        )}
                    </Panel>

                    {(order.tags?.length > 0 || attributes.length > 0) && (
                        <Panel title="Tags and attributes">
                            {order.tags?.length > 0 && (
                                <div className={classes.tags}>
                                    {order.tags.map((tag: string, index: number) => <Chip key={index} label={tag} size="small" />)}
                                </div>
                            )}
                            {attributes.length > 0 && (
                                <KeyValueList items={attributes.map(([key, value]) => ({ label: key, value: String(value) }))} />
                            )}
                        </Panel>
                    )}
                </div>

                <aside className={classes.side} aria-label="Order summary">
                    <Panel
                        title="Summary"
                        padding="sm"
                        bodyClassName={classes.summaryBody}
                        footer={(
                            <div className={classes.totals}>
                                <div className={classes.totalRow}><span>Subtotal</span><span className={classes.totalValue}>{money(order.subtotalPrice, currency)}</span></div>
                                <div className={classes.totalRow}><span>Shipping</span><span className={classes.totalValue}>{money(order.totalShippingPrice, currency)}</span></div>
                                <div className={classes.totalRow}><span>Tax</span><span className={classes.totalValue}>{money(order.totalTax, currency)}</span></div>
                                <div className={`${classes.totalRow} ${classes.grandTotal}`}><span>Total</span><span className={classes.totalValue}>{money(order.totalPrice, currency)}</span></div>
                            </div>
                        )}
                    >
                        <KeyValueList
                            minColumnWidth={120}
                            items={[
                                { label: 'Financial', value: order.financialStatus ? <span className={classes.capitalize}>{order.financialStatus}</span> : null },
                                { label: 'Fulfillment', value: order.fulfillmentStatus ? <span className={classes.capitalize}>{order.fulfillmentStatus}</span> : null },
                                { label: 'Shipping method', value: order.shippingLineTitle },
                                { label: 'Order ID', value: order.id, mono: true },
                                ...(order.closedAt ? [{ label: 'Closed', value: formatDate(order.closedAt, { time: true }) }] : []),
                                ...(order.cancelledAt ? [{ label: 'Cancelled', value: formatDate(order.cancelledAt, { time: true }) }] : []),
                                { label: 'Synced', value: formatDate(order.syncedAt, { time: true }) },
                            ]}
                        />
                        {order.note && (
                            <div style={{ marginTop: 14 }}>
                                <div className={classes.secondary} style={{ marginBottom: 4 }}>Note</div>
                                <p className={classes.note}>{order.note}</p>
                            </div>
                        )}
                    </Panel>

                    <Panel title="Customer">
                        <KeyValueList
                            stacked
                            items={[
                                { label: 'Name', value: customerName },
                                { label: 'Email', value: order.customerEmail || order.email },
                                { label: 'Phone', value: order.phone },
                                { label: 'Customer ID', value: order.customerId, mono: true },
                                ...(order.customer ? [
                                    { label: 'Total spent', value: money(order.customer.totalSpent, currency) },
                                    { label: 'Orders', value: order.customer.ordersCount },
                                    { label: 'Accepts marketing', value: order.customer.acceptsMarketing ? 'Yes' : 'No' },
                                    ...(order.customer.tags ? [{ label: 'Tags', value: order.customer.tags }] : []),
                                ] : []),
                            ]}
                        />
                    </Panel>

                    {address && (
                        <Panel title="Shipping address">
                            <address className={classes.address}>
                                <b>{[address.firstName, address.lastName].filter(Boolean).join(' ') || '—'}</b><br />
                                {address.company && <>{address.company}<br /></>}
                                {address.address1 && <>{address.address1}<br /></>}
                                {address.address2 && <>{address.address2}<br /></>}
                                {[address.city, address.province || address.provinceCode, address.zip].filter(Boolean).join(', ')}
                                {(address.city || address.province || address.zip) && <br />}
                                {[address.country, address.countryCode ? `(${address.countryCode})` : ''].filter(Boolean).join(' ')}
                                {address.phone && <><br />{address.phone}</>}
                            </address>
                        </Panel>
                    )}
                </aside>
            </div>
        </PageContainer>
    );
};

export default OneAavOrder;
